import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import CTABand from "../components/CTABand";
import { Home, Bus, Scale, ArrowRight, Check } from "lucide-react";
import { PROGRAMS } from "../data/pages";
import { PHOTOS } from "../data/photos";

const ICONS = { Home, Bus, Scale };

const ProgramsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <PageHero
          eyebrow="What We Do"
          title="Programs built on lived experience."
          description="Our programs meet people where they are — whether at the very beginning of their journey or further along a sustainable path. They are the heart of our organization, made possible by our Certified Recovery Peer Specialists. People truly make the programs."
          breadcrumbs={[{ label: "Programs" }]}
          image={PHOTOS.groupTherapy}
        />

        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="grid md:grid-cols-3 gap-6">
              {PROGRAMS.map((p, i) => {
                const Icon = ICONS[p.icon] || Home;
                return (
                  <Link
                    key={p.slug}
                    to={`/programs/${p.slug}`}
                    className="rc-card-hover group bg-white border border-[hsl(var(--border))] rounded-2xl overflow-hidden flex flex-col"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--rc-deep))]/70 via-transparent to-transparent" />
                      <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[hsl(var(--rc-purple))]" />
                      </div>
                      <div className="absolute bottom-4 left-4 text-[11px] tracking-[0.2em] uppercase text-white/85 font-semibold">
                        Program 0{i + 1}
                      </div>
                    </div>
                    <div className="p-7 flex-1 flex flex-col">
                      <h3 className="font-serif-display text-[24px] font-semibold text-[hsl(var(--rc-deep))] group-hover:text-[hsl(var(--rc-purple))] transition-colors">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-[14.5px] leading-[1.65] text-[hsl(var(--rc-slate))] flex-1">
                        {p.short}
                      </p>
                      <div className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold text-[hsl(var(--rc-purple))]">
                        Learn more
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-24 rc-section-soft">
          <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                What unites our programs
              </p>
              <h2 className="font-serif-display mt-3 text-[34px] md:text-[42px] leading-[1.1] font-semibold text-[hsl(var(--rc-deep))]">
                Different settings. One promise.
              </h2>
              <p className="mt-5 text-[16px] leading-[1.75] text-[hsl(var(--rc-slate))]">
                From neighborhood community centers to mobile outreach to the courthouse, every program is grounded in the same commitment: meet people where they are, walk with them, and connect them to multiple pathways for sustainable recovery.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {[
                "Free, confidential and judgment-free",
                "Led by Certified Recovery Peer Specialists",
                "Multiple pathways supported",
                "Family and loved-one resources",
                "Connection to housing, employment, healthcare",
                "Long-term, beyond-the-crisis support",
              ].map((t) => (
                <div
                  key={t}
                  className="flex items-start gap-3 bg-white border border-[hsl(var(--border))] rounded-xl p-4"
                >
                  <div className="w-7 h-7 rounded-full bg-[hsl(var(--rc-purple-soft))] flex items-center justify-center shrink-0 text-[hsl(var(--rc-purple))]">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                  <p className="text-[14px] leading-[1.55] text-[hsl(var(--rc-charcoal))] font-medium">
                    {t}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABand />
      </main>
      <Footer />
    </div>
  );
};

export default ProgramsPage;
